import { prepareZXingModule, readBarcodes, type ReaderOptions } from "zxing-wasm/reader";
import { formatById, toZxingFormats } from "../formats.js";
import type { ScanResult } from "../types.js";

export interface ZxingEngineOptions {
  /** Override where the .wasm binary is fetched from (defaults to jsDelivr CDN via zxing-wasm). */
  wasmLocateFile?: (path: string, prefix: string) => string;
  maxNumberOfSymbols?: number;
}

/**
 * Thin, typed wrapper around zxing-wasm that speaks the SDK's own
 * `ScanResult`/format-id vocabulary instead of leaking the underlying
 * library's types through the public API — keeping the OSS engine
 * swappable without breaking consumers.
 */
export class ZxingEngine {
  private ready: Promise<void>;

  constructor(options: ZxingEngineOptions = {}) {
    if (options.wasmLocateFile) {
      prepareZXingModule({ overrides: { locateFile: options.wasmLocateFile } });
    }
    this.ready = prepareZXingModule({ fireImmediately: true }).then(() => undefined);
  }

  async warmUp(): Promise<void> {
    await this.ready;
  }

  async decode(
    imageData: ImageData,
    enabledFormatIds: string[],
    opts: { maxNumberOfSymbols?: number; tryHarder?: boolean; minLineCount?: number } = {},
  ): Promise<Omit<ScanResult, "enhanced" | "timestamp">[]> {
    await this.ready;
    const formats = toZxingFormats(enabledFormatIds);
    const readerOptions: ReaderOptions = {
      formats,
      tryHarder: opts.tryHarder ?? true,
      maxNumberOfSymbols: opts.maxNumberOfSymbols ?? 5,
      // zxing's default of 2 is enough for linear formats (esp. GS1 DataBar)
      // to occasionally validate against pure noise once many formats are
      // enabled at once with tryHarder on; 3 costs real scans almost
      // nothing (any decodable frame has far more than 3 usable scan lines)
      // but meaningfully cuts false-accept risk on degraded frames.
      minLineCount: opts.minLineCount ?? 3,
    };
    const results = await readBarcodes(imageData, readerOptions);
    return results
      .filter((r) => r.format !== "None" && r.isValid !== false)
      .map((r) => ({
        text: r.text,
        bytes: r.bytes,
        format: formatIdFromZxing(r.format),
        position: r.position,
      }));
  }
}

const ZXING_TO_SDK_FORMAT: Record<string, string> = {
  Codabar: "codabar",
  Code39: "code39",
  Code93: "code93",
  Code128: "code128",
  DataBar: "databar",
  DataBarLimited: "databar",
  DataBarExpanded: "databar-expanded",
  "EAN-13": "ean13",
  "EAN-8": "ean8",
  ITF: "itf",
  "UPC-A": "upca",
  "UPC-E": "upce",
  Aztec: "aztec",
  DataMatrix: "datamatrix",
  MaxiCode: "maxicode",
  PDF417: "pdf417",
  QRCode: "qrcode",
  MicroQRCode: "microqr",
  rMQRCode: "rmqr",
};

function formatIdFromZxing(format: string): string {
  return ZXING_TO_SDK_FORMAT[format] ?? formatById(format.toLowerCase())?.id ?? format.toLowerCase();
}
